import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaBell } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import PersonalizedPlanForm from '@/components/PersonalizedPlanForm';
import FormUpdateComponent from '@/components/FormUpdateComponent';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PersonalizedFormPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [planStatus, setPlanStatus] = useState(null);
  const [formUpdateStatus, setFormUpdateStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if we're in update mode
  const isUpdateMode = searchParams.get('mode') === 'update';

  useEffect(() => {
    checkExistingPlan();
  }, [user]);

  // Auto-redirect al Dashboard si el plan ya está listo (evita ciclo Dashboard →
  // /my-diet → 'Llenar formulario' → /personalized-form → 'Ver mi dieta' → ciclo).
  useEffect(() => {
    if (!loading && planStatus?.has_plan && (planStatus.status === 'sent' || planStatus.status === 'ready')) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, planStatus, navigate]);

  const checkExistingPlan = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/personalized-plans/check-status/${encodeURIComponent(user.email)}`);
      setPlanStatus(response.data);
      
      // If user has a plan, check for form updates
      if (response.data?.has_plan && user?.subscription_plan) {
        const updateCheck = await axios.get(
          `${API}/api/personalized-plans/form-update-check/${encodeURIComponent(user.email)}?plan_type=${user.subscription_plan}`
        );
        setFormUpdateStatus(updateCheck.data);
      }
    } catch (error) {
      console.error('Error checking plan status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormComplete = (response) => {
    // Redirigir al dashboard después de completar
    setTimeout(() => {
      navigate('/dashboard');
    }, 3000);
  };
  
  const handleUpdateComplete = () => {
    // Refrescar el estado y volver al dashboard
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  // Si estamos en modo actualización y hay preguntas nuevas
  if (isUpdateMode && formUpdateStatus?.has_pending_update) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-white mb-6 hover:bg-white/20"
          >
            <FaArrowLeft className="mr-2" /> Volver al Dashboard
          </Button>
          
          <FormUpdateComponent 
            user={user}
            formUpdateStatus={formUpdateStatus}
            onComplete={handleUpdateComplete}
          />
        </div>
      </div>
    );
  }

  // Si ya tiene un plan ENVIADO/LISTO, mostrar loader mientras el useEffect redirige.
  if (!loading && planStatus?.has_plan && (planStatus.status === 'sent' || planStatus.status === 'ready')) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <p className="text-white">Redirigiendo a tu dashboard...</p>
      </div>
    );
  }
  
  // Si tiene un plan EN PROCESO (pendiente/preparando/completado pero no enviado), mostrar mensaje de espera
  if (!loading && planStatus?.has_plan && (planStatus.status === 'pending' || planStatus.status === 'preparing')) {
    const isFitEvolution = planStatus.status === 'preparing';
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-2xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-white mb-6 hover:bg-white/20"
          >
            <FaArrowLeft className="mr-2" /> Volver al Dashboard
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-8 text-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="plan-preparing-message"
          >
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(234, 179, 8, 0.2)' }}>
              <span className="text-4xl animate-bounce">⏳</span>
            </div>
            <h2 className="text-2xl font-bold text-yellow-400 mb-4">
              {isFitEvolution ? 'Tu Fit Evolution está en preparación' : 'Tu Plan Está en Preparación'}
            </h2>
            <p className="text-gray-400 mb-6">
              Ya completaste tu formulario. Deisy está trabajando en tu plan personalizado.
              <br />
              <strong className="text-white">
                {isFitEvolution ? 'Recuerda que tu plan estará listo 24 horas después de que adjuntes tus fotos iniciales. ¡Adjúntalas ya!' : 'Estará listo en máximo 48 horas.'}
              </strong>
            </p>
            {isFitEvolution && (
              <Button 
                onClick={() => navigate('/fit-evolution/preparing')}
                className="text-white font-bold rounded-full mr-3"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                data-testid="view-countdown-btn"
              >
                Ver countdown de 24h
              </Button>
            )}
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 rounded-full"
            >
              Volver al Dashboard
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Mostrar formulario si no tiene plan
  // IMPORTANTE: NUNCA usar fallback a 'premium' — si el subscription_plan del usuario
  // no viene definido, mostramos un mensaje de error. Antes había un fallback a 'premium'
  // que causaba que clientas Fit Evolution recibieran el formulario premium por error.
  const userPlan = user?.subscription_plan;
  if (!userPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-md text-center rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-white mb-2">No pudimos identificar tu plan</h2>
          <p className="text-gray-400 mb-6 text-sm">
            Contacta a soporte para que asignen correctamente tu plan (Fit Evolution / Premium / Gold).
          </p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-3xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="text-white mb-6 hover:bg-white/20"
        >
          <FaArrowLeft className="mr-2" /> Volver al Dashboard
        </Button>
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">
            Formulario de Datos Personales
          </h1>
          <p className="text-white/80 text-lg">
            Completa este formulario para que podamos crear tu plan 100% personalizado
          </p>
        </motion.div>
        
        <PersonalizedPlanForm 
          planType={userPlan} 
          onComplete={handleFormComplete}
        />
      </div>
    </div>
  );
};

export default PersonalizedFormPage;
